'use client'

import React from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowLeft, PencilSimple } from 'phosphor-react'
import { Link } from '@/lib'
import Components from '../components'

const illustrations = [
  {
    title: 'Bird Collage',
    image: '/assets/creative-projects/Birdcollage.JPG',
    description: 'Mixed-media collage.'
  },
  {
    title: 'Color This - Cover',
    image: '/assets/creative-projects/Color-This-1.jpeg',
    description: 'Cover illustrations for surrealist coloring book.'
  },
  {
    title: 'Color This Illustration',
    image: '/assets/creative-projects/Color-This-2.jpeg',
    description: 'Surrealist coloring book illustration'
  },
  {
    title: 'Pizza Love',
    image: '/assets/creative-projects/Pizza Love.png',
    description: 'Digital illustration of pizza heart, because I love pizza.'
  },
  {
    title: 'Peace\'s for the Species \'08 Poster',
    image: '/assets/creative-projects/Poster Red.png',
    description: 'Event poster design for a solar-powered and waste-free community event featuring local bands and activities.'
  },
  {
    title: 'Skull Drawing',
    image: '/assets/creative-projects/Skulldrawing.JPG',
    description: 'Charcoal on paper.'
  },
  {
    title: 'Tettegouche State Park',
    image: '/assets/creative-projects/TettegoucheMNP.jpg',
    description: 'Watercolor and Ink on paper.'
  },
  {
    title: 'Pagardio',
    image: '/assets/creative-projects/pagardio.jpg',
    description: 'Children\'s book about Pagardio the turtle.'
  }
]

const acupunctureIllustrations = [
  {
    title: 'Gallbladder 20',
    image: '/assets/creative-projects/Acuatwork-Blog-Illustration-Gallbadder-20.png',
    description: 'Acupuncture point card illustration.'
  },
  {
    title: 'Gallbladder 21',
    image: '/assets/creative-projects/Acuatwork-Blog-Illustration-Gallbadder-21.png',
    description: 'Acupuncture point card illustration.'
  },
  {
    title: 'Kidney 3',
    image: '/assets/creative-projects/Acuatwork-Blog-Illustration-Kidney-3.png',
    description: 'Acupuncture point card illustration.'
  },
  {
    title: 'Large Intestine 4',
    image: '/assets/creative-projects/Acuatwork-Blog-Illustration-Large-Intestine-4.png',
    description: 'Acupuncture point card illustration.'
  },
  {
    title: 'Spleen 6',
    image: '/assets/creative-projects/Acuatwork-Blog-Illustration-Spleen-6.png',
    description: 'Acupuncture point card illustration.'
  },
  {
    title: 'Stomach 36',
    image: '/assets/creative-projects/Acuatwork-Blog-Illustration-Stomach-36.png',
    description: 'Acupuncture point card illustration.'
  }
]

export default function DrawingIllustration() {

  return (
    <Components.PageContainer>
      <Components.SkipLink />
      <Components.GlassNavigation />
      
      <main id="main-content" tabIndex={-1} className="pt-16 md:pt-32 pb-16 px-4 md:px-6" role="main" aria-label="Drawing and Illustration">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <header className="mb-8 md:mb-12">
            <Button
              asChild
              variant="ghost"
              className="mb-6 text-[var(--brutal-ink)] hover:bg-[var(--brutal-panel)]"
            >
              <Link to="/CreativeProjects">
                <ArrowLeft weight="duotone" className="w-4 h-4 mr-2" />
                Back to Creative Projects
              </Link>
            </Button>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 aspect-square bg-gradient-to-br from-[var(--brutal-mustard)] to-[var(--brutal-kraft)] rounded-full flex items-center justify-center flex-shrink-0">
                <PencilSimple weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--brutal-ink)] leading-tight break-words">
                  Drawing & Illustration
                </h1>
                <p className="text-base md:text-lg text-[var(--brutal-muted)] mt-2 break-words">
                  Pen and ink drawings, character illustrations, and narrative visual storytelling
                </p>
              </div>
            </div>
          </header>

          {/* Gallery Grid */}
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" aria-label="Illustration gallery">
            {/* Acupuncture Illustrations - Combined Card with Static Image */}
            <Card className="group relative overflow-hidden brutal-panel hover:bg-[var(--brutal-kraft)] transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl !p-0 !gap-0">
              <Components.ImageModal
                src={acupunctureIllustrations[0].image}
                alt="Acupuncture Point Cards"
              >
                <div className="relative aspect-[4/3] overflow-hidden border-b-2 border-[var(--brutal-ink)] cursor-pointer">
                  <img
                    src={acupunctureIllustrations[0].image}
                    alt="Acupuncture Point Cards"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </Components.ImageModal>
              <div className="p-4">
                <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-1 group-hover:text-[var(--brutal-ink)] transition-colors duration-200 break-words">
                  Acupuncture Point Cards
                </h3>
                <p className="text-[var(--brutal-muted)] text-sm leading-relaxed break-words">
                  Series of acupuncture point card illustrations for educational content.
                </p>
              </div>
            </Card>

            {/* Other Illustrations */}
            {illustrations.map((illustration, index) => {
              const isPDF = illustration.image.toLowerCase().endsWith('.pdf')
              
              return (
                <Card
                  key={index}
                  className="group relative overflow-hidden brutal-panel hover:bg-[var(--brutal-kraft)] transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl !p-0 !gap-0"
                >
                  {isPDF ? (
                    <div className="relative aspect-[4/3] overflow-hidden border-b-2 border-[var(--brutal-ink)] bg-white">
                      <iframe
                        src={`${illustration.image}#toolbar=0&navpanes=0&scrollbar=0&zoom=page-fit`}
                        className="w-full h-full border-0 pointer-events-none"
                        title={illustration.title}
                        loading="lazy"
                        scrolling="no"
                        style={{ overflow: 'hidden' }}
                      />
                    </div>
                  ) : (
                    <Components.ImageModal
                      src={illustration.image}
                      alt={illustration.title}
                    >
                      <div className="relative aspect-[4/3] overflow-hidden border-b-2 border-[var(--brutal-ink)] cursor-pointer">
                        <img
                          src={illustration.image}
                          alt={illustration.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                    </Components.ImageModal>
                  )}
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-1 group-hover:text-[var(--brutal-ink)] transition-colors duration-200 break-words">
                      {illustration.title}
                    </h3>
                    <p className="text-[var(--brutal-muted)] text-sm leading-relaxed break-words">
                      {illustration.description}
                    </p>
                  </div>
                </Card>
              )
            })}
          </section>
        </div>
      </main>
    </Components.PageContainer>
  )
}
